import { CAST_AI } from '@cast/constants';
import { Radio, Typography, Stack, tokens } from '@cast/design-system';
import { KarpenterLogo } from '@cast/features-cluster-ui';

import { LogoSmallSvg } from 'assets/svg';
import { notReachable } from 'utils/types/not-reachable';

import { AutoscalerType } from '../../../types';

type Props = {
  autoscalerType: AutoscalerType;
  checked: boolean;
  testId?: string;
  onClick: () => void;
};

const autoscalerTypeToTitleMap: Record<AutoscalerType, string> = {
  karpenter: 'Karpenter',
  'cast-ai': `${CAST_AI} autoscaler`,
};

type AutoscalerIconProps = {
  autoscalerType: AutoscalerType;
};

const AutoscalerIcon = ({ autoscalerType }: AutoscalerIconProps) => {
  switch (autoscalerType) {
    case 'karpenter':
      return <KarpenterLogo size={20} />;
    case 'cast-ai':
      return <LogoSmallSvg width={16} height={16} />;
    default:
      return notReachable(autoscalerType);
  }
};

const autoscalerTypeToDescriptionMap: Record<AutoscalerType, string> = {
  karpenter: `Keeps Karpenter as your node provisioner. ${CAST_AI} adds automation.`,
  'cast-ai': `${CAST_AI} provisions and manages your cluster's nodes.`,
};

export const SelectAutoscalerTypeRadio = ({
  autoscalerType,
  testId,
  onClick,
  checked,
}: Props) => {
  return (
    <Stack
      onClick={onClick}
      gap={2}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        pt: 8,
        pb: 12,
        px: 8,
        borderRadius: '4px',
        border: '1px solid',
        borderColor: checked
          ? tokens.theme.border.selection.primary
          : tokens.theme.border.structural.neutral,
      }}
      role="radio"
    >
      <Stack direction="row" gap={6} alignItems="center">
        <Radio testId={testId} size="small" checked={checked} />
        <Stack gap={6} alignItems="center" direction="row">
          <AutoscalerIcon autoscalerType={autoscalerType} />
          <Typography
            variant="P12B"
            color={checked ? tokens.theme.fg.action.primary : undefined}
          >
            {autoscalerTypeToTitleMap[autoscalerType]}
          </Typography>
        </Stack>
      </Stack>

      <Typography variant="P10R" color="textSecondary" sx={{ pl: 24 }}>
        {autoscalerTypeToDescriptionMap[autoscalerType]}
      </Typography>
    </Stack>
  );
};
